import React, { useEffect, useState } from 'react';
import { dbService, type Profile, type AgentInfo } from '../lib/database';
import type { UserRole } from '../lib/auth';
import { Users, AlertTriangle, CheckCircle2, XCircle, UserCircle, Building2, Calendar, Shield, Trash2, Plus, Save, X } from 'lucide-react';
import { format } from 'date-fns';

export function AdminUsersList() {
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<UserRole>('user');
  const [editActive, setEditActive] = useState(false);
  const [editCompany, setEditCompany] = useState('');
  const [savingUser, setSavingUser] = useState(false);
  const [addAgentUserId, setAddAgentUserId] = useState<string | null>(null);
  const [newAgentId, setNewAgentId] = useState('');
  const [newAgentName, setNewAgentName] = useState('');

  const fetchUsers = async () => {
    try {
      const data = await dbService.getAllProfiles();
      setUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchUsers();
  };

  const startEdit = (user: Profile) => {
    setEditingUser(user.id);
    setEditRole(user.role);
    setEditActive(user.is_active);
    setEditCompany(user.company_name || '');
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setAddAgentUserId(null);
    setNewAgentId('');
    setNewAgentName('');
  };

  const saveUser = async (userId: string) => {
    setSavingUser(true);
    try {
      await dbService.updateProfile(userId, {
        role: editRole,
        is_active: editActive,
        company_name: editCompany || null,
      });
      await fetchUsers();
      setEditingUser(null);
    } catch (err) {
      console.error('Error saving user:', err);
    } finally {
      setSavingUser(false);
    }
  };

  const handleAddAgent = async (userId: string) => {
    if (!newAgentId.trim() || !newAgentName.trim()) return;
    try {
      const agent: AgentInfo = { agent_id: newAgentId.trim(), display_name: newAgentName.trim() };
      await dbService.addAgentToProfile(userId, agent);
      await dbService.addAgent(agent);
      await fetchUsers();
      setAddAgentUserId(null);
      setNewAgentId('');
      setNewAgentName('');
    } catch (err) {
      console.error('Error adding agent:', err);
    }
  };

  const handleRemoveAgent = async (userId: string, agentId: string) => {
    try {
      await dbService.removeAgentFromProfile(userId, agentId);
      await fetchUsers();
    } catch (err) {
      console.error('Error removing agent:', err);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse flex items-center justify-center">
          <div className="text-zinc-400">Loading users...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-500 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-emerald-400" />
          <h2 className="text-2xl font-bold text-white">Users</h2>
          <button
            onClick={handleRefresh}
            className={`p-1.5 rounded-full hover:bg-zinc-800 transition-all ${
              isRefreshing ? 'opacity-50' : ''
            }`}
            title="Refresh users"
          >
            <Users className={`w-5 h-5 text-emerald-400 ${
              isRefreshing ? 'animate-spin' : ''
            }`} />
          </button>
        </div>
        <div className="bg-zinc-800/50 px-4 py-2 rounded-lg border border-zinc-700/50">
          <span className="text-zinc-400">Total Users: </span>
          <span className="text-emerald-400 font-semibold">{users.length}</span>
        </div>
      </div>

      <div className="grid gap-6">
        {users.map((user) => (
          <div
            key={user.id}
            className="bg-zinc-800/50 rounded-lg p-6 border border-zinc-700/50 hover:border-emerald-500/30 transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-lg font-semibold text-white">
                    {user.company_name || 'Unnamed Company'}
                  </h3>
                  <p className="text-zinc-400">{user.email}</p>
                  {user.full_name && (
                    <div className="flex items-center text-zinc-300 text-sm">
                      <UserCircle className="w-4 h-4 mr-1.5 text-emerald-400" />
                      <span>{user.full_name}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm">
                    <Calendar className="w-4 h-4 mr-1.5 text-emerald-400" />
                    <span className="text-zinc-400">
                      Joined {format(new Date(user.created_at), 'MMM d, yyyy')}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-2">
                  {user.is_active ? (
                    <div className="flex items-center px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-sm border border-emerald-500/20">
                      <CheckCircle2 className="w-4 h-4 mr-1.5" />
                      <span>Active</span>
                    </div>
                  ) : (
                    <div className="flex items-center px-3 py-1 bg-red-500/10 text-red-400 rounded-full text-sm border border-red-500/20">
                      <XCircle className="w-4 h-4 mr-1.5" />
                      <span>Inactive</span>
                    </div>
                  )}
                  <div className="flex items-center px-3 py-1 bg-zinc-700/50 text-zinc-300 rounded-full text-sm border border-zinc-600/50">
                    <Shield className="w-3 h-3 mr-1.5" />
                    <span className="capitalize">{user.role}</span>
                  </div>
                </div>
                {editingUser !== user.id && (
                  <button
                    onClick={() => startEdit(user)}
                    className="text-xs text-zinc-500 hover:text-emerald-400 transition"
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>

            {editingUser === user.id && (
              <div className="mt-4 p-4 bg-zinc-900/50 rounded-lg border border-zinc-700/50 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Role</label>
                    <select
                      value={editRole}
                      onChange={e => setEditRole(e.target.value as UserRole)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                    >
                      <option value="user">User</option>
                      <option value="support">Support</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Status</label>
                    <select
                      value={editActive ? 'active' : 'inactive'}
                      onChange={e => setEditActive(e.target.value === 'active')}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Company</label>
                    <input
                      type="text"
                      value={editCompany}
                      onChange={e => setEditCompany(e.target.value)}
                      placeholder="Company name"
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={cancelEdit}
                    className="px-3 py-1.5 text-sm text-zinc-400 hover:text-white transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => saveUser(user.id)}
                    disabled={savingUser}
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-500 text-black text-sm rounded-lg hover:bg-emerald-400 transition disabled:opacity-50"
                  >
                    <Save className="w-3.5 h-3.5" />
                    {savingUser ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            )}

            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-zinc-400">Assigned Agents</h4>
                {addAgentUserId !== user.id && (
                  <button
                    onClick={() => setAddAgentUserId(user.id)}
                    className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add Agent
                  </button>
                )}
              </div>

              {addAgentUserId === user.id && (
                <div className="mb-3 p-3 bg-zinc-900/50 rounded-lg border border-zinc-700/50">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                    <input
                      type="text"
                      value={newAgentId}
                      onChange={e => setNewAgentId(e.target.value)}
                      placeholder="Agent ID"
                      className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                    />
                    <input
                      type="text"
                      value={newAgentName}
                      onChange={e => setNewAgentName(e.target.value)}
                      placeholder="Display Name"
                      className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => { setAddAgentUserId(null); setNewAgentId(''); setNewAgentName(''); }}
                      className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleAddAgent(user.id)}
                      disabled={!newAgentId.trim() || !newAgentName.trim()}
                      className="px-3 py-1.5 bg-emerald-500 text-black text-xs rounded-lg hover:bg-emerald-400 transition disabled:opacity-50"
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}

              {user.agents_info && user.agents_info.length > 0 ? (
                <div className="grid gap-3 md:grid-cols-2">
                  {user.agents_info.map((agent) => (
                    <div
                      key={agent.agent_id}
                      className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-800 hover:border-emerald-500/20 transition-all group"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-zinc-300 font-medium">
                            {agent.display_name}
                          </p>
                          <p className="text-sm text-zinc-500">
                            ID: {agent.agent_id}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemoveAgent(user.id, agent.agent_id)}
                          className="opacity-0 group-hover:opacity-100 p-1.5 text-zinc-500 hover:text-red-400 transition-all"
                          title="Remove agent"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
                  <p className="text-zinc-500 text-sm">No agents assigned</p>
                </div>
              )}
            </div>
          </div>
        ))}

        {users.length === 0 && (
          <div className="text-center py-12 bg-zinc-800/30 rounded-lg border border-zinc-700/30">
            <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No users found</p>
          </div>
        )}
      </div>
    </div>
  );
}
